import * as React from 'react';
import { Fragment } from 'react';
import cn from 'classnames';
import { createBemBlockBuilder } from '@ebsco-ui/utils';
import _pt from 'prop-types';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons/faAngleDown';
import Button from '@ebsco-ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { jsx, jsxs } from 'react/jsx-runtime';

var DataTableColumn = function DataTableColumn() {
  return null;
};

DataTableColumn.propTypes = {
  className: _pt.string,
  header: _pt.func.isRequired,
  headerClassName: _pt.string,
  fieldName: _pt.string.isRequired,
  formatter: _pt.func.isRequired,
  footer: _pt.func,
  footerClassName: _pt.string,
  tagName: _pt.oneOf(["td", "th"])
};
DataTableColumn.displayName = "DataTable.Column";

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var _excluded = ["className", "open", "id", "onClick"];

var DataTableToggle = function DataTableToggle(_ref) {
  var className = _ref.className,
      _ref$open = _ref.open,
      open = _ref$open === void 0 ? false : _ref$open,
      id = _ref.id,
      onClick = _ref.onClick,
      props = _objectWithoutProperties(_ref, _excluded);

  var getBlocksWith = createBemBlockBuilder(["eb-data-table", className]);
  var defaultTitle = open ? "Collapse" : "Expand";
  return /*#__PURE__*/jsx(Button, {
    "aria-label": props.ariaLabel,
    "aria-describedby": props.ariaDescribedBy,
    "aria-controls": "result".concat(id),
    "aria-expanded": open ? true : false,
    className: cn(getBlocksWith("__toggle-btn"), open ? getBlocksWith("__toggle-btn--is-open") : getBlocksWith("__toggle-btn--is-closed")),
    onClick: onClick,
    styleType: "flat-icon",
    children: /*#__PURE__*/jsx(FontAwesomeIcon, {
      size: "2x",
      icon: faAngleDown,
      title: props.title || defaultTitle,
      "data-auto": "toggle-icon"
    })
  });
};

DataTableToggle.propTypes = {
  className: _pt.string,
  open: _pt.bool.isRequired,
  onClick: _pt.func
};

var DataTableToggleRowFormatter = function DataTableToggleRowFormatter(props) {
  var index = props.index,
      cells = props.cells,
      item = props.item,
      getBlocksWith = props.getBlocksWith;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("tr", {
      className: getBlocksWith("__body-row"),
      "data-auto": "table-row",
      children: cells
    }, "data_row_".concat(index)), item.open && /*#__PURE__*/jsx("tr", {
      id: "result".concat(index),
      className: getBlocksWith("__body-row"),
      "data-auto": "table-expanded-row",
      children: /*#__PURE__*/jsx("td", {
        className: cn(getBlocksWith("__cell"), getBlocksWith("__cell--expanded")),
        colSpan: 99,
        "data-auto": "table-expanded-data",
        children: item.renderRowContent(index)
      })
    }, "data_row_2_".concat(index))]
  }, "data_".concat(index));
};

var DefaultRowFormatter = function DefaultRowFormatter(_ref) {
  var index = _ref.index,
      cells = _ref.cells,
      getBlocksWith = _ref.getBlocksWith;
  return /*#__PURE__*/jsx("tr", {
    className: getBlocksWith("__body-row"),
    "data-auto": "table-row",
    children: cells
  }, "data_row_".concat(index));
};

var DataTable = React.forwardRef(function DataTable(_ref2, ref) {
  var dataGetter = _ref2.dataGetter,
      children = _ref2.children,
      _ref2$rowFormatter = _ref2.rowFormatter,
      rowFormatter = _ref2$rowFormatter === void 0 ? DefaultRowFormatter : _ref2$rowFormatter,
      caption = _ref2.caption,
      className = _ref2.className,
      _ref2$isFocusable = _ref2.isFocusable,
      isFocusable = _ref2$isFocusable === void 0 ? true : _ref2$isFocusable;
  var getBlocksWith = createBemBlockBuilder(["eb-data-table", className]);

  var renderRow = function renderRow(item, index) {
    var cells = React.Children.map(children, function (child) {
      if (!child) {
        return null;
      }

      var _child$props = child.props,
          tagName = _child$props.tagName,
          fieldName = _child$props.fieldName;
      var getCellBlocksWith = createBemBlockBuilder([child.props.className]);
      var dataClasses = cn(getBlocksWith("__cell"), getCellBlocksWith("__cell")); // @ts-expect-error c.type.displayName is not a member of the React.Element type.

      if (child.type.displayName !== "DataTable.Column") {
        console.warn("DataTable only uses children of type DataTable.Column. Other component types will be ignored.");
        return null;
      }

      var Tag = tagName || "td";
      return /*#__PURE__*/jsx(Tag, {
        className: dataClasses,
        "data-auto": "table-data",
        children: child.props.formatter(item)
      }, fieldName);
    });
    return rowFormatter({
      item: item,
      cells: cells,
      index: index,
      getBlocksWith: getBlocksWith
    });
  };

  var renderRows = function renderRows() {
    var index = 0;
    var rows = [];
    var res;
    var row = true;

    while (row) {
      row = dataGetter(index);
      res = row ? renderRow(row, index) : row;

      if (row) {
        rows.push(res);
      }

      index += 1;
    }

    return rows;
  }; // render headers, account for sorting


  var renderHeader = function renderHeader(render, fieldName) {
    return render({
      fieldName: fieldName
    });
  };

  var hasHeaders = false;
  var headers = React.Children.map(children, function (child) {
    if (!child) {
      return null;
    }

    var _child$props2 = child.props,
        header = _child$props2.header,
        headerClassName = _child$props2.headerClassName,
        fieldName = _child$props2.fieldName;
    var getHeaderBlocksWith = createBemBlockBuilder([headerClassName]);
    var headerClasses = cn(getBlocksWith("__header-cell"), getHeaderBlocksWith("__header-cell"));

    if (!header) {
      return /*#__PURE__*/jsx("th", {}, fieldName);
    }

    hasHeaders = true;
    return /*#__PURE__*/jsx("th", {
      className: headerClasses,
      "data-auto": "table-column-header",
      children: renderHeader(header, fieldName)
    }, fieldName);
  }); //render footers

  var renderFooter = function renderFooter(render, fieldName) {
    return render({
      fieldName: fieldName
    });
  };

  var hasFooters = false;
  var footers = React.Children.map(children, function (child) {
    if (!child) {
      return null;
    }

    var _child$props3 = child.props,
        footer = _child$props3.footer,
        fieldName = _child$props3.fieldName,
        footerClassName = _child$props3.footerClassName;
    var getFooterBlocksWith = createBemBlockBuilder([footerClassName]);
    var footerClasses = cn(getBlocksWith("__footer-cell"), getFooterBlocksWith("__footer-cell"));

    if (!footer) {
      return /*#__PURE__*/jsx("td", {}, fieldName);
    }

    hasFooters = true;
    return /*#__PURE__*/jsx("td", {
      className: footerClasses,
      "data-auto": "table-column-footer",
      children: renderFooter(footer, fieldName)
    }, fieldName);
  });
  return /*#__PURE__*/jsx("div", {
    className: getBlocksWith(),
    tabIndex: isFocusable ? 0 : undefined,
    "data-auto": "table-wrapper",
    children: /*#__PURE__*/jsxs("table", {
      className: getBlocksWith("__table"),
      "data-auto": "table-self",
      ref: ref,
      children: [caption && /*#__PURE__*/jsx("caption", {
        className: getBlocksWith("__caption"),
        "data-auto": "table-caption",
        children: caption
      }), hasHeaders && /*#__PURE__*/jsx("thead", {
        className: getBlocksWith("__header-section"),
        "data-auto": "table-headers",
        children: /*#__PURE__*/jsx("tr", {
          className: getBlocksWith("__header-row"),
          children: headers
        })
      }), /*#__PURE__*/jsx("tbody", {
        className: getBlocksWith("__body"),
        "data-auto": "table-body",
        children: renderRows()
      }), hasFooters && /*#__PURE__*/jsx("tfoot", {
        className: getBlocksWith("__footer-section"),
        "data-auto": "table-footers",
        children: /*#__PURE__*/jsx("tr", {
          className: getBlocksWith("__footer-row"),
          children: footers
        })
      })]
    })
  });
});
DataTable.displayName = "DataTable";
DataTable.Column = DataTableColumn;
DataTable.Column.displayName = "DataTable.Column";
DataTable.Toggle = DataTableToggle;
DataTable.Toggle.displayName = "DataTable.Toggle";
DataTable.ToggleRowFormatter = DataTableToggleRowFormatter;

export { DataTable as default };
//# sourceMappingURL=data-table-esm.js.map
